import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { udhuseSubpages, nybyggeriSubpages, serviceSubpages } from '../data';

export default function Services() {
  const location = useLocation();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);

  // Extract the current slug from pathname
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const currentSlug = pathSegments[pathSegments.length - 1] || 'vi-tilbyder';

  useEffect(() => {
    if (currentSlug === 'vi-tilbyder') {
      setLoading(false);
      return;
    }

    setLoading(true);
    fetch('https://api.dev.auxo.dk/public/content/ydelser')
      .then(res => res.json())
      .then(data => {
        // Find match in CMS data by slug or title
        const match = data.find(item => {
          const itemSlug = item.slug?.da || item.slug || item.id;
          return (
            itemSlug === currentSlug ||
            itemSlug === currentSlug.replace(/-/g, '') ||
            (currentSlug === 'garage-og-carport' && (itemSlug === 'garage' || itemSlug === 'carport')) ||
            (currentSlug === 'beklaedning-og-udhaeng' && (itemSlug === 'beklaedning-udhaeng' || itemSlug === 'udhaeng')) ||
            (currentSlug === 'koekken-og-interioer' && itemSlug === 'koekken-interioer') ||
            (currentSlug === 'terrasser-og-uderum' && itemSlug === 'terrasse-uderum')
          );
        });

        if (match && localServicesData[currentSlug]) {
          // Merge CMS gallery with updated text from localData for exact copy compliance
          setService({
            ...match,
            titel: localServicesData[currentSlug].titel,
            beskrivelse: localServicesData[currentSlug].beskrivelse,
            hero_billede: match.hero_billede || localServicesData[currentSlug].hero_billede,
            galleri: match.galleri && match.galleri.length > 0 ? match.galleri : localServicesData[currentSlug].galleri
          });
        } else if (match) {
          setService(match);
        } else if (localServicesData[currentSlug]) {
          setService(localServicesData[currentSlug]);
        } else {
          setService(null);
        }
      })
      .catch(err => {
        console.error('Error fetching service from CMS:', err);
        setService(localServicesData[currentSlug] || null);
      })
      .finally(() => setLoading(false));
  }, [currentSlug]);

  const isUdhuseCategory = currentSlug === 'udhuse';
  const isNybyggeriCategory = currentSlug === 'nybyggeri-i-trae';
  const isServiceCategory = currentSlug === 'service';

  // 1. MAIN SERVICES OVERVIEW PAGE (/vi-tilbyder)
  if (currentSlug === 'vi-tilbyder') {
    return (
      <div className="bg-white py-24">
        <SEO />
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 flex flex-col gap-4">
            <span className="text-accent font-semibold tracking-[0.2em] uppercase text-xs">
              Vores ekspertise
            </span>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-stone-900">
              Vi tilbyder
            </h1>
            <p className="text-stone-600 text-base sm:text-lg leading-relaxed">
              Hos Munkgaard Træbyg mestrer vi alle facetter af tømrerfaget. Vi har særligt fokus på træbyggeri, der forener æstetik og holdbarhed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ServiceCard 
              title="Udhuse" 
              href="/vi-tilbyder/udhuse" 
              img="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250716154846.jpg"
              desc="Garager, carporte, annekser og skure i træ. Solide konstruktioner, funktionalitet og gennemtænkte detaljer tilpasset boligen."
            />
            <ServiceCard 
              title="Tilbygning" 
              href="/vi-tilbyder/tilbygning" 
              img="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260609-075822.jpg"
              desc="Udvid den eksisterende bolig med ekstra plads, lysindfald og en gennemtænkt planløsning, der matcher det oprindelige hus."
            />
            <ServiceCard 
              title="Nybyggeri i træ" 
              href="/vi-tilbyder/nybyggeri-i-trae" 
              img="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260716-170800.jpg"
              desc="Opførelse af komplette bygninger med træ som centralt og bærende material: Sommerhuse og helårshuse med stor designfrihed."
            />
            <ServiceCard 
              title="Totalrenovering" 
              href="/vi-tilbyder/totalrenovering" 
              img="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260211-154328.jpg"
              desc="Gennemgribende renovering og modernisering af ældre boliger med respekt for husets eksisterende karakter og fremtidssikring."
            />
            <ServiceCard 
              title="Service" 
              href="/vi-tilbyder/service" 
              img="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214057.jpg"
              desc="Professionel tømrerservice til alle opgaver: Gulv, vag, loft, tag, beklædning, udhæng, køkken, døre, vinduer og terrasser."
            />
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <i className="fa-solid fa-spinner animate-spin text-4xl text-accent" />
        <span className="text-stone-500 text-sm font-medium">Henter indhold...</span>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="bg-white min-h-[60vh] flex flex-col items-center justify-center gap-6 px-6 text-center">
        <h1 className="font-display text-3xl font-bold text-stone-900">Ydelsen blev ikke fundet</h1>
        <p className="text-stone-500 max-w-md">Beklager, men den ydelse du leder efter findes ikke, eller er blevet flyttet.</p>
        <Link to="/vi-tilbyder" className="bg-primary hover:bg-primary-light text-white px-6 py-3 rounded-md text-sm font-semibold tracking-wide transition-all duration-300">
          Se alle ydelser
        </Link>
      </div>
    );
  }

  const titleText = service.titel?.da || service.titel;
  const descHtml = service.beskrivelse?.da || service.beskrivelse;
  const heroImage = service.hero_billede?.[0] || 'https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260716-170800.jpg';
  const resizedHero = `https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/${heroImage.replace('https://assets.auxodev.dk/', '')}`;

  return (
    <div className="bg-white">
      <SEO dynamicTitle={`${titleText} | Munkgaard Træbyg ApS`} />

      {/* Hero Header */}
      <section className="relative py-24 bg-stone-900 text-white">
        <div className="absolute inset-0 z-0">
          <img 
            src={resizedHero} 
            alt={titleText} 
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/40 to-transparent" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center flex flex-col gap-4">
          <span className="text-white font-semibold tracking-[0.2em] uppercase text-xs">Vi tilbyder</span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white">
            {titleText}
          </h1>
        </div>
      </section>

      {/* Content & Form Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Main Text Side */}
          <div className="lg:col-span-7 flex flex-col gap-8">
            <div 
              className="text-stone-600 leading-relaxed space-y-6 text-base sm:text-lg"
              dangerouslySetInnerHTML={{ __html: descHtml }}
            />

            {/* If Udhuse Category, show subpages grid */}
            {isUdhuseCategory && (
              <div className="mt-12 pt-12 border-t border-stone-100 flex flex-col gap-8">
                <h3 className="font-display text-2xl font-bold text-stone-900">Underpunkter for Udhuse</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {udhuseSubpages.map((sub) => (
                    <div key={sub.slug} className="bg-stone-50 p-6 rounded-xl border border-stone-200/60 flex flex-col gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-accent text-lg shadow-sm border border-stone-200">
                        <i className={sub.icon} />
                      </div>
                      <h4 className="font-display text-xl font-bold text-stone-900">{sub.title}</h4>
                      <p className="text-stone-600 text-sm leading-relaxed line-clamp-3">{sub.description}</p>
                      <Link 
                        to={`/vi-tilbyder/udhuse/${sub.slug}`}
                        className="text-accent font-semibold text-xs hover:underline mt-2 inline-flex items-center gap-1"
                      >
                        <span>Læs mere om {sub.title.toLowerCase()}</span>
                        <i className="fa-solid fa-arrow-right text-[10px]" />
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* If Nybyggeri Category, show subpages grid */}
            {isNybyggeriCategory && (
              <div className="mt-12 pt-12 border-t border-stone-100 flex flex-col gap-8">
                <h3 className="font-display text-2xl font-bold text-stone-900">Underpunkter for Nybyggeri i træ</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {nybyggeriSubpages.map((sub) => (
                    <div key={sub.slug} className="bg-stone-50 p-6 rounded-xl border border-stone-200/60 flex flex-col gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-accent text-lg shadow-sm border border-stone-200">
                        <i className={sub.icon} />
                      </div>
                      <h4 className="font-display text-xl font-bold text-stone-900">{sub.title}</h4>
                      <p className="text-stone-600 text-sm leading-relaxed line-clamp-3">{sub.description}</p>
                      <Link 
                        to={`/vi-tilbyder/nybyggeri-i-trae/${sub.slug}`}
                        className="text-accent font-semibold text-xs hover:underline mt-2 inline-flex items-center gap-1"
                      >
                        <span>Læs mere om {sub.title.toLowerCase()}</span>
                        <i className="fa-solid fa-arrow-right text-[10px]" />
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* If Service Category, show subpages grid */}
            {isServiceCategory && (
              <div className="mt-12 pt-12 border-t border-stone-100 flex flex-col gap-8">
                <h3 className="font-display text-2xl font-bold text-stone-900">Underpunkter for Service</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {serviceSubpages.map((sub) => (
                    <div key={sub.slug} className="bg-stone-50 p-6 rounded-xl border border-stone-200/60 flex flex-col gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-accent text-lg shadow-sm border border-stone-200">
                        <i className={sub.icon} />
                      </div>
                      <h4 className="font-display text-xl font-bold text-stone-900">{sub.title}</h4>
                      <p className="text-stone-600 text-sm leading-relaxed line-clamp-3">{sub.description}</p>
                      <Link 
                        to={`/vi-tilbyder/service/${sub.slug}`}
                        className="text-accent font-semibold text-xs hover:underline mt-2 inline-flex items-center gap-1"
                      >
                        <span>Læs mere om {sub.title.toLowerCase()}</span>
                        <i className="fa-solid fa-arrow-right text-[10px]" />
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Gallery Section */}
            {service.galleri && service.galleri.length > 0 && (
              <div className="mt-16 pt-12 border-t border-stone-100">
                <h3 className="font-display text-2xl font-bold text-stone-900 mb-6">Projektgalleri</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {service.galleri.map((img, idx) => {
                    const thumbUrl = `https://assets.auxodev.dk/cdn-cgi/image/width=300,height=300,fit=cover,quality=80,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`;
                    return (
                      <div 
                        key={idx} 
                        className="aspect-square rounded-lg overflow-hidden border border-stone-200 shadow-sm cursor-pointer group relative"
                        onClick={() => setActiveImage(img)}
                      >
                        <img 
                          src={thumbUrl} 
                          alt={`Galleri billede ${idx + 1}`} 
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-stone-950/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <i className="fa-solid fa-magnifying-glass-plus text-white text-lg" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Form Column */}
          <div className="lg:col-span-5">
            <div className="sticky top-28">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setActiveImage(null)}
        >
          <button 
            onClick={() => setActiveImage(null)}
            className="absolute top-6 right-6 text-white hover:text-accent text-3xl focus:outline-none cursor-pointer"
            aria-label="Luk galleri"
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <img 
            src={activeImage} 
            alt="Galleri stor visning" 
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}

function ServiceCard({ title, href, img, desc }) {
  return (
    <Link to={href} className="group bg-white rounded-xl overflow-hidden border border-stone-200 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col">
      <div className="aspect-[16/10] overflow-hidden">
        <img 
          src={img} 
          alt={title} 
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="p-6 flex flex-col gap-2 flex-grow">
        <h3 className="font-display text-xl font-bold text-stone-900 group-hover:text-accent transition-colors">{title}</h3>
        <p className="text-stone-500 text-sm leading-relaxed line-clamp-3">{desc}</p>
        <span className="text-accent font-semibold text-sm mt-auto pt-4 inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform">
          Læs mere <i className="fa-solid fa-arrow-right text-xs" />
        </span>
      </div>
    </Link>
  );
}

// Complete local services data matching all new slugs with accurate Danish text
const localServicesData = {
  "udhuse": {
    titel: "Udhuse",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250716154846.jpg"],
    beskrivelse: "<p>Udhuse er ikke bare praktiske opbevaringsrum – de kan også være med til at skabe en flot arkitektonisk sammenhæng mellem boligen, haven og grunden. Hos Munkgaard Træbyg ApS bygger vi alt fra garager og carporte til isolerede annekser, redskabsskure og hobbyværksteder.</p><p>Vi bygger med fokus på solide trækonstruktioner, gennemtænkte detaljer, funktionalitet og et design, der passer naturally til din eksisterende bolig. Uanset om du har brug for en lukket garage med god plads, et stilrent skur eller et fristed i haven, sørger vi for et solidt stykke tømrerhåndværk i høj kvalitet.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250716154846.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260424-081002.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250818111542.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250818111533.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251111-120209.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250925-191507.jpg"
    ]
  },
  "garage-og-carport": {
    titel: "Garage og carport",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250818111542.jpg"],
    beskrivelse: "<p>En garage eller carport er mere end bare et overdække til bilen. Det er en investering i hverdagens komfort og boligens samlede udtryk. Hos Munkgaard Træbyg bygger vi både åbne carporte, lukkede garager og kombinerede løsninger med integreret redskabsrum eller hobbyværksted.</p><p>Vi rådgiver om den optimale placering på grunden, gode materialevalg i træ og sørger for, at linjeføring og tagkonstruktion harmonerer med hovedhuset. Hvad enten du ønsker en isoleret garage med plads til opbevaring eller en minimalistisk carport, leverer vi et gennemført resultat.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250818111542.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250818111533.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251111-120209.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250925-191507.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250925-191431_337e9d44.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250925-191419.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250925-191357.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250918-163619.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250910-131635.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250909-161514.jpg"
    ]
  },
  "anneks": {
    titel: "Anneks",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260609-075737.jpg"],
    beskrivelse: "<p>Har du brug for ekstra gæsteplads, et privat hjemmekontor eller et hyggeligt fristed i haven? Et gennemtænkt anneks i træ skaber nye og værdifulde kvadratmeter på din grund.</p><p>Vi bygger annekser i høj håndværksmæssig kvalitet, der kan isoleres og indrettes til helårsbrug. Vi tilpasser arkitekturen, vinduespartierne og træbeklædningen, så annekset udgør en smuk og naturlig forlængelse af din helårsbolig eller dit sommerhus.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260609-075737.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260417-132312.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260417-132230.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260327-110541.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260310-142612_6192842c.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260309-084326_e8f7cfee.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260306-140529.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260303-093118.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260303-093036_5b25fb10.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260302-134755.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260226-145537.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260217-135141.jpg"
    ]
  },
  "skur": {
    titel: "Skur",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260424-081002.jpg"],
    beskrivelse: "<p>Et solidt redskabsskur er uundværligt til opbevaring af haveredskaber, cykler, plæneklipper og havemøbler. Vi opfører skræddersyede skure i kvalitetstræ, der modstår det danske vejr og fremstår pæne år efter år.</p><p>Vi rådgiver om optimal rumfordeling, ventilation, dørstørrelser og holdbare træsorter, så skuret dækker dine praktiske behov og matcher havens æstetik.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250716154846.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250714111755.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250711132325.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250709121920.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/fb-img-1786002055674_378fd255.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/fb-img-1786002042595.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260424-081002.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260424-080930.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250916-081755.jpg"
    ]
  },
  "tilbygning": {
    titel: "Tilbygning",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260609-075822.jpg"],
    beskrivelse: "<p>Skal familien have mere plads, kan en gennemtænkt tilbygning i træ ofte være en langt bedre løsning end at flytte fra det hjem og det kvarter, du glæder dig over.</p><p>Når vi bygger tilbygninger, fokuserer vi på, at udvidelsen skal føles som en naturlig og sammenhængende del af det eksisterende hus – både udefra og indefra. Træ er et varmt, naturligt og holdbart byggemateriale, som giver fantastiske muligheder for at skabe et godt lysindfald, funktionelle rum og en tæt byggeproces fra råhus til færdig finish.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260609-075822.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260417-132325.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260417-132221.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250925-191338_384e8a27.jpg"
    ]
  },
  "nybyggeri-i-trae": {
    titel: "Nybyggeri i træ",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260716-170800.jpg"],
    beskrivelse: "<p>Træ er et af de mest alsidige, smukke og konstruktionsmæssigt stærke byggematerialer, vi har. Hos Munkgaard Træbyg bygger vi komplette nye bygninger, hvor træ indgår som det centrale og bærende element.</p><p>Vi opfører både sommerhuse og moderne helårshuse. Nybyggeri i træ giver enestående arkitektoniske muligheder, stor designfrihed, et behageligt indeklima og mulighed for at træffe mere bæredygtige materialevalg. Vi sikrer præcist håndværk og stærke konstruktioner, der holder i mange årtier.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260716-170800.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214047.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260309-152705.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img-6188_eff36820.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240827150458.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240825100302.jpg"
    ]
  },
  "sommerhus": {
    titel: "Sommerhus",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260716-170800.jpg"],
    beskrivelse: "<p>Et sommerhus i træ skaber den perfekte ramme om ro, nærvær og feriestunder i naturen. Træ som byggemateriale passer helt naturligt ind i det danske sommerhuslandskab og giver et varmt og indbydende udtryk.</p><p>Vi rådgiver om gennemtænkte løsninger, der udnytter lysindfaldet, skaber gode overgange til terrassen og sikrer en robust konstruktion over for det danske vejr. Hvad enten du ønsker et klassisk sommerhusudtryk eller et moderne design med store glaspartier, tilbyder vi tæt personlig dialog og løsninger tilpasset din grund og dine behov.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240825100343.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240825095528-1_1325211a.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240825095453.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240819201510_46721e54.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20231115150255_1e7a2bdb.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260716-170800.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214047.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260309-152705.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260303-093036_df44c8cb.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251113-163817.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250910-131635_5ed49b92.jpg"
    ]
  },
  "helaarshus": {
    titel: "Helårshus",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img-6188_eff36820.jpg"],
    beskrivelse: "<p>Et moderne helårshus bygget med træ som bærende materiale er et utroligt smukt, behageligt og gennemtænkt alternativ til et traditionelt murstenshus. Træbyggeri åbner for stor designfrihed, smuk arkitektur og et sundt indeklima.</p><p>Trækonstruktioner kan opføres med stor præcision, og materialet kan være et mere bæredygtigt materialevalg sammenlignet med tungere byggeformer. Vi hjælper dig trygt gennem overvejelserne og sikrer en solid konstruktion fra fundament til tag.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img-6188_eff36820.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240827150458.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240825100302.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240825095743.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240825095607.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240825095528.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240825095506.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240824193854.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240819201510.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240706162622.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240514091528.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240214121433.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240210151736.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20231206153236.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20231115150255.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20231106114009.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/fb-img-1749010660183_17774820.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/fb-img-1749010620379_178d560f.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/fb-img-1749010582351_f2d9f2e3.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/fb-img-1742934877402_d41b5f71.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/fb-img-1742934853981_78d94f4d.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251118-085903-1.jpg"
    ]
  },
  "totalrenovering": {
    titel: "Totalrenovering",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260211-154328.jpg"],
    beskrivelse: "<p>En totalrenovering kan give et ældre hus nyt liv, bedre energimæssig ydeevne og en moderne planløsning – samtidig med at husets oprindelige charme og karakter bevares.</p><p>Hos Munkgaard Træbyg tilbyder vi gennemgribende renoveringer med fokus på moderne byggeteknikker, holdbarhed og høj håndværksmæssig finish. Som enkeltmandsvirksomhed har du direkte dialog med den tømrer, der udfører arbejdet, hvilket sikrer overblik og tryghed gennem hele projektet.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250709081056.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250709081021.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250709080959.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250708190344.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250603164027_25a884bb.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250520160204_b29916ce.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250520151605.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250430164835.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250327114018_29886df4.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20241031082540.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20241024085230.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20241016133603.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20241010080707.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240810144401.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240810083127.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240802115738.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240703121016.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260211-154328.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260211-154310.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260211-154248.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260211-154221.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260211-154209.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260130-131520.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260129-103047.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260127-154041.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260113-152344.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251217-112804.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251216-160002.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251118-104617-1_97b30389.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251118-104532-1.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251118-101640.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251118-101615.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251118-085903_ddd39f09.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251118-085903-1_900d567c.jpg"
    ]
  },
  "service": {
    titel: "Service",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214057.jpg"],
    beskrivelse: "<p>Hos Munkgaard Træbyg skelner vi ikke mellem store og mindre opgaver. Mindre tømreropgaver og løbende vedligeholdelse af din bolig får nøjagtig samme serisøe behandling, ordentlighed og høje faglighed som et stort nybyggeri.</p><p>Vores serviceområder dækker alt fra gulv, væg og loft til tag, træbeklædning, køkkener, vinduer, døre og udendørs terrasser. Vi møder til tiden, rydder op og leverer pålideligt håndværk.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214057.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250821071223.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251118-104617-1.jpg"
    ]
  },
  "gulv-vaeg-loft": {
    titel: "Gulv/væg/loft",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260417-132325_61049784.jpg"],
    beskrivelse: "<p>Nye gulve, lofter og vægbeklædninger gør en markant forskel for boligens indeklima og udtryk. Vi lægger alle typer trægulve og monterer lofter – herunder gipslofter og akustiklofter som Troldtekt – med stor præcision.</p><p>Vi sikrer snorlige linjer, korrekte underkonstruktioner og pæne afslutninger ved gerigter og fodlister.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260417-132325_61049784.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260126-111844.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251125-112450.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250918-163650.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250709081013.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250709080959_49cd206b.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250606150942.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250605095004.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250603163832.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250520160300.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240302113603.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240210151713.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240131131524-01.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/fb-img-1749010620379_418a43ec.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/fb-img-1742934877402_64e22523.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250605113518.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250103174256.jpg"
    ]
  },
  "tag": {
    titel: "Tag",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250821071223.jpg"],
    beskrivelse: "<p>Taget beskytter dit hus mod regn, sne og blæst. Vi udfører alt tømrerarbejde på tagkonstruktioner, udskiftning af råddent træværk i vindskeder, reparationer, løbende vedligehold samt montering af nye tagrender og udhæng.</p><p>Vi rådgiver om holdbare materialer, der kan modstå det danske klima i mange år.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250821071223.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250820161929.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250820074127.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250707183256_4496a1d8.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250707103300.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250603164112_533958f8.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250430172556_3a8c4e92.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240426111149.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260604-161205.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250925-191338.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250827-143445.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240510180333.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240328131349.jpg"
    ]
  },
  "beklaedning-og-udhaeng": {
    titel: "Beklædning og udhæng",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250603164112.jpg"],
    beskrivelse: "<p>Træbeklædning på facader og udhæng løfter bygningens udseende og beskytter underliggende konstruktioner mod vejrliget. Vi udfører både lodret og vandret beklædning samt tilpasning af udhæng i høj håndværksmæssig kvalitet.</p><p>Vi rådgiver om egnede træsorter og korrekt ventilation bag beklædningen for maksimal levetid.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250603164112.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250603164043.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250603164027.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250603164006.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250603163946.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250520160235.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20241114113104.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20241114113052.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20241114113046.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20241114104922.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20241108110731.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20241108092027.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/fb-img-1786002055674.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260310-142612.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251118-085903.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251118-075821.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250925-191431.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250925-155450.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250910-131638.jpg"
    ]
  },
  "koekken-og-interioer": {
    titel: "Køkken og interiør",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251118-104617-1.jpg"],
    beskrivelse: "<p>Køkkenet er samlingspunktet i boligen. Vi tilbyder professionel montering af nye køkkenelementer samt tilpasninger og renovering af eksisterende inventar.</p><p>Derudover udfører vi snedker- og tømrerprægede interiørløsninger som indbyggede reoler, bænke og speciallavede skabe, der udnytter pladsen i boligen perfekt.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/snapchat-409147383.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250709081240.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250709081224.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250605113518.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250103174256.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240825100315.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240825095607_3104b8a8.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240825095528-1.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240625173011.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240505105127.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240422121052.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240417162208.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240415112647.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/fb-img-1749010660183_10e684c6.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251118-104617-1.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251031-155923.jpg"
    ]
  },
  "doere-vinduer": {
    titel: "Døre/vinduer",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214057.jpg"],
    beskrivelse: "<p>Nye døre og vinduer forbedrer boligens energivenlighed, fjerner træk og giver et æstetisk løft. Vi står for udskiftning og montering af døre og vinduer med stor omhu.</p><p>Vi sørger for præcis opmåling, tætning, korrekte lysninger og pæne lister, der respekterer husets stil.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250707183256.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250520160204.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250430172556.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20250327114018.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214057.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251114-092244.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251113-164046.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251113-163824.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20251110-145627.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20250916-081751.jpg"
    ]
  },
  "terrasser-og-uderum": {
    titel: "Terrasser og uderum",
    hero_billede: ["https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214047_461d7779.jpg"],
    beskrivelse: "<p>Nyd sommeren på en veludført træterrasse. Vi bygger terrasser i alle størrelser og træsorter, tilpasset havens terræn, niveauforskelle og udgange fra huset.</p><p>Vi designer og opfører også overdækkede terrasser, pergolaer og læhegn, der skaber skønne udendørs opholdsrum.</p>",
    galleri: [
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240827150458_e14e547c.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240819201533.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240818205354.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240818205316.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240510180333.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/img20240328131349.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260716-170909.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260716-170809.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260716-170758.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214637.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214625.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214116.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214057_b80cf7a9.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260707-214047_461d7779.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260702-164306.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260625-112308.jpg",
      "https://assets.auxodev.dk/assets/eb528839-41b2-4674-9198-41967ffbbdbc/20260618-122657.jpg"
    ]
  }
};
